import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_BEST = "doggo-loader-best-treats";

function readBest() {
  try {
    return Math.max(0, Math.floor(Number(localStorage.getItem(STORAGE_BEST) || 0)));
  } catch {
    return 0;
  }
}

function writeBestIfHigher(n) {
  try {
    const prev = readBest();
    if (n > prev) localStorage.setItem(STORAGE_BEST, String(n));
  } catch {
    /* ignore */
  }
}

const DOG_MIN = 10;
const DOG_MAX = 90;
/** Keyboard nudge per keypress; dog eases toward target (see SMOOTH). */
const MOVE_STEP = 5;
/** Each frame: move this fraction of the way toward the target (0–1). */
const SMOOTH = 0.26;
const TICK_MS = 28;
const FALL_PX = 5;
const SPAWN_MS = 950;
const MAX_PIECES = 14;
const CATCH_MARGIN_PX = 38;
/** Vertical size used for catch math (keep in sync with `.app-loader__falling-inner` height). */
const BONE_H = 28;
/** Fall past the lane bottom without a catch → miss; this many misses ends the run. */
const MAX_MISSES = 5;
/** Bones spawn only after the dog leaves center by this much (%) or the player taps/drags the lane. */
const ARM_SPAWN_DOG_PCT = 2.5;

/**
 * `animation` — idle patrol dog (initial load). `game` — catch bones after Start (manual / infinite loader).
 * @param {() => void} [onReturnToSlides] — game mode only: dismiss loader and show slides.
 */
export function LoaderDogGame({ variant = "animation", onReturnToSlides }) {
  const isGame = variant === "game";
  const [started, setStarted] = useState(false);
  const gameActive = isGame && started;
  const laneRef = useRef(null);
  const [dogPct, setDogPct] = useState(50);
  const dogPctRef = useRef(50);
  const dogTargetRef = useRef(50);
  const lastPaintDogRef = useRef(50);
  const smoothRafRef = useRef(0);
  /** Pieces live in a ref so the tick loop is never fighting React batching. */
  const piecesRef = useRef([]);
  const nextId = useRef(0);
  const [, setFrame] = useState(0);
  const forceRender = useCallback(() => {
    setFrame((n) => (n + 1) & 1023);
  }, []);

  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [best, setBest] = useState(() => readBest());
  const dragging = useRef(false);
  /** Emoji usually faces left; "right" = mirrored so the dog looks toward higher x. */
  const [facing, setFacing] = useState("left");
  const [lean, setLean] = useState(0);
  const leanTimerRef = useRef(0);
  const lastDragXRef = useRef(null);
  const [catchGlow, setCatchGlow] = useState(false);
  const catchGlowTimerRef = useRef(0);
  /** False as soon as the run ends (stop, game over, or Return) so spawn/tick ignore stragglers. */
  const playOnRef = useRef(false);
  /** True once bones are allowed to spawn (movement from center or lane pointer). */
  const spawnArmedRef = useRef(false);
  /** First pointer down on the lane this run (not on the dog) counts as “ready” to spawn. */
  const lanePointerEngagedRef = useRef(false);
  const missesRef = useRef(0);
  const [misses, setMisses] = useState(0);
  const [lobbyNotice, setLobbyNotice] = useState(null);
  /** After first Start this session, keep best run visible in the lobby between runs. */
  const [everStartedRun, setEverStartedRun] = useState(false);
  const returnToLobbyRef = useRef(() => {});

  useEffect(() => {
    scoreRef.current = score;
    setBest((b) => Math.max(b, score));
  }, [score]);

  useEffect(() => {
    return () => {
      writeBestIfHigher(scoreRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      window.clearTimeout(leanTimerRef.current);
      window.clearTimeout(catchGlowTimerRef.current);
    };
  }, []);

  /** Smooth dog position toward `dogTargetRef` every frame (keys + drag feel eased). */
  useEffect(() => {
    if (!gameActive) return undefined;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const k = reduceMotion ? 1 : SMOOTH;
    const loop = () => {
      const target = dogTargetRef.current;
      let cur = dogPctRef.current;
      const diff = target - cur;
      if (Math.abs(diff) < 0.035 || k >= 1) {
        cur = target;
      } else {
        cur += diff * k;
      }
      dogPctRef.current = cur;
      const rounded = Math.round(cur * 200) / 200;
      if (Math.abs(rounded - lastPaintDogRef.current) > 0.004) {
        lastPaintDogRef.current = rounded;
        setDogPct(rounded);
      }
      smoothRafRef.current = requestAnimationFrame(loop);
    };
    smoothRafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(smoothRafRef.current);
  }, [gameActive]);

  const applyMoveStyle = useCallback((delta) => {
    if (delta === 0) return;
    setFacing(delta > 0 ? "right" : "left");
    setLean(delta > 0 ? 12 : -12);
    window.clearTimeout(leanTimerRef.current);
    leanTimerRef.current = window.setTimeout(() => setLean(0), 260);
  }, []);

  const tryArmSpawn = useCallback(() => {
    if (spawnArmedRef.current || !playOnRef.current) return;
    if (
      lanePointerEngagedRef.current ||
      Math.abs(dogTargetRef.current - 50) >= ARM_SPAWN_DOG_PCT ||
      Math.abs(dogPctRef.current - 50) >= ARM_SPAWN_DOG_PCT
    ) {
      spawnArmedRef.current = true;
      forceRender();
    }
  }, [forceRender]);

  const nudgeDog = useCallback(
    (delta) => {
      applyMoveStyle(delta);
      dogTargetRef.current = Math.min(DOG_MAX, Math.max(DOG_MIN, dogTargetRef.current + delta));
      tryArmSpawn();
    },
    [applyMoveStyle, tryArmSpawn]
  );

  const setDogFromClientX = useCallback(
    (clientX) => {
      const lane = laneRef.current;
      if (!lane) return;
      const r = lane.getBoundingClientRect();
      const pct = ((clientX - r.left) / Math.max(1, r.width)) * 100;
      const clamped = Math.min(DOG_MAX, Math.max(DOG_MIN, pct));
      const prevX = lastDragXRef.current;
      if (prevX !== null) {
        const dx = clientX - prevX;
        if (Math.abs(dx) > 1.5) applyMoveStyle(dx);
      }
      lastDragXRef.current = clientX;
      dogTargetRef.current = clamped;
      tryArmSpawn();
    },
    [applyMoveStyle, tryArmSpawn]
  );

  useEffect(() => {
    if (!gameActive) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") {
        e.preventDefault();
        e.stopPropagation();
        nudgeDog(-MOVE_STEP);
      } else if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") {
        e.preventDefault();
        e.stopPropagation();
        nudgeDog(MOVE_STEP);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [gameActive, nudgeDog]);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      const t = e.target;
      if (t instanceof Element && t.closest(".app-loader__lane-dog")) return;
      lanePointerEngagedRef.current = true;
      dragging.current = true;
      lastDragXRef.current = e.clientX;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDogFromClientX(e.clientX);
    },
    [setDogFromClientX]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!dragging.current) return;
      setDogFromClientX(e.clientX);
    },
    [setDogFromClientX]
  );

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    lastDragXRef.current = null;
  }, []);

  const resetLobbyDog = useCallback(() => {
    dogTargetRef.current = 50;
    dogPctRef.current = 50;
    lastPaintDogRef.current = 50;
    setDogPct(50);
    setFacing("left");
    setLean(0);
    dragging.current = false;
    lastDragXRef.current = null;
  }, []);

  const returnToLobby = useCallback(
    ({ fromMaxMisses } = {}) => {
      playOnRef.current = false;
      spawnArmedRef.current = false;
      lanePointerEngagedRef.current = false;
      writeBestIfHigher(scoreRef.current);
      piecesRef.current = [];
      nextId.current = 0;
      scoreRef.current = 0;
      setScore(0);
      setCatchGlow(false);
      window.clearTimeout(catchGlowTimerRef.current);
      resetLobbyDog();
      missesRef.current = 0;
      setMisses(0);
      setLobbyNotice(fromMaxMisses ? "Game over — 5 bones missed." : null);
      setStarted(false);
      forceRender();
    },
    [resetLobbyDog, forceRender]
  );

  const onStartGame = useCallback(() => {
    setEverStartedRun(true);
    setLobbyNotice(null);
    piecesRef.current = [];
    nextId.current = 0;
    scoreRef.current = 0;
    setScore(0);
    resetLobbyDog();
    missesRef.current = 0;
    setMisses(0);
    spawnArmedRef.current = false;
    lanePointerEngagedRef.current = false;
    playOnRef.current = true;
    setStarted(true);
  }, [resetLobbyDog]);

  const onStopGame = useCallback(() => {
    returnToLobby({ fromMaxMisses: false });
  }, [returnToLobby]);

  returnToLobbyRef.current = returnToLobby;

  useEffect(() => {
    if (!gameActive) return undefined;
    playOnRef.current = true;
    const spawnId = window.setInterval(() => {
      if (!playOnRef.current) return;
      if (!spawnArmedRef.current) return;
      if (piecesRef.current.length >= MAX_PIECES) return;
      nextId.current += 1;
      piecesRef.current = [
        ...piecesRef.current,
        { id: nextId.current, xPct: 14 + Math.random() * 72, y: -6 },
      ];
      forceRender();
    }, SPAWN_MS);

    const tickId = window.setInterval(() => {
      if (!playOnRef.current) return;
      const lane = laneRef.current;
      const rawH = lane?.clientHeight ?? 0;
      const rawW = lane?.clientWidth ?? 0;
      const h = rawH >= 48 ? rawH : 108;
      const w = rawW >= 80 ? rawW : 280;
      const dogPx = (dogPctRef.current / 100) * w;
      const fallOutY = h + BONE_H + 20;

      const prev = piecesRef.current;
      const next = [];
      let gained = 0;
      let missedThisTick = 0;
      for (const p of prev) {
        const ny = p.y + FALL_PX;
        const px = (p.xPct / 100) * w;
        const boneBottom = ny + BONE_H;
        const nearBottom = boneBottom >= h - 48 && ny <= h + 6;
        const aligned = Math.abs(px - dogPx) < CATCH_MARGIN_PX;
        if (nearBottom && aligned) {
          gained += 1;
          continue;
        }
        if (ny < fallOutY) next.push({ ...p, y: ny });
        else missedThisTick += 1;
      }
      piecesRef.current = next;

      if (missedThisTick > 0) {
        missesRef.current += missedThisTick;
        if (missesRef.current >= MAX_MISSES) {
          missesRef.current = MAX_MISSES;
          setMisses(MAX_MISSES);
          returnToLobbyRef.current({ fromMaxMisses: true });
          return;
        }
        setMisses(missesRef.current);
      }

      if (gained > 0) {
        setScore((s) => s + gained);
        setCatchGlow(true);
        window.clearTimeout(catchGlowTimerRef.current);
        catchGlowTimerRef.current = window.setTimeout(() => setCatchGlow(false), 160);
      }
      forceRender();
    }, TICK_MS);

    return () => {
      window.clearInterval(spawnId);
      window.clearInterval(tickId);
    };
  }, [gameActive, forceRender]);

  const stored = readBest();
  const showBest = Math.max(best, score, stored);
  const pieces = piecesRef.current;

  const laneClass = [
    "app-loader__lane",
    !isGame ? "app-loader__lane--ambient" : "",
    gameActive ? "app-loader__lane--play" : "",
    catchGlow && gameActive ? "app-loader__lane--catch" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const dogClass = [
    "app-loader__lane-dog",
    !isGame ? "app-loader__lane-dog--patrol" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="app-loader__game">
      {gameActive ? (
        <>
          <p className="app-loader__game-hint">
            Bones fall from the top — slide the dog under them to catch. ← → or A/D, or drag the lane. Game ends
            after {MAX_MISSES} misses.
          </p>
          {!spawnArmedRef.current ? (
            <p className="app-loader__game-hint app-loader__game-hint--spawn-wait">
              Move the dog a little, or tap / drag the lane, to start dropping bones.
            </p>
          ) : null}
        </>
      ) : isGame ? (
        <p
          className="app-loader__game-hint app-loader__game-hint--idle"
          aria-live={lobbyNotice ? "polite" : undefined}
        >
          {lobbyNotice ??
            "Press Start to play catch, Return to go back to the slides."}
        </p>
      ) : null}
      <div
        ref={laneRef}
        className={laneClass}
        role={gameActive ? "application" : isGame ? "region" : undefined}
        aria-label={
          gameActive
            ? `Catch falling bones by moving the dog under them. Bones begin after you move the dog or use the lane. Game ends after ${MAX_MISSES} misses.`
            : isGame
              ? "Optional catch game. Press Start to play."
              : undefined
        }
        aria-hidden={!isGame ? true : undefined}
        onPointerDown={gameActive ? onPointerDown : undefined}
        onPointerMove={gameActive ? onPointerMove : undefined}
        onPointerUp={gameActive ? onPointerUp : undefined}
        onPointerCancel={gameActive ? onPointerUp : undefined}
      >
        {gameActive &&
          pieces.map((p) => (
            <span
              key={p.id}
              className="app-loader__falling"
              style={{ left: `${p.xPct}%`, top: p.y }}
              aria-hidden="true"
            >
              <span className="app-loader__falling-inner">🦴</span>
            </span>
          ))}
        <div
          className={dogClass}
          style={isGame ? { left: `${dogPct}%` } : undefined}
          aria-hidden="true"
        >
          <span
            className="app-loader__dog-actor"
            style={
              isGame
                ? {
                    transform: `${facing === "right" ? "scaleX(-1)" : "scaleX(1)"} rotate(${lean}deg)`,
                  }
                : undefined
            }
          >
            <span className="app-loader__dog">🐕</span>
          </span>
        </div>
      </div>
      {isGame && onReturnToSlides ? (
        <div className="app-loader__game-actions">
          {gameActive ? (
            <button type="button" className="app-loader__btn-stop-game" onClick={onStopGame}>
              Stop game
            </button>
          ) : (
            <button type="button" className="app-loader__start" onClick={onStartGame}>
              Start
            </button>
          )}
          <button
            type="button"
            className="app-loader__btn-return"
            onClick={onReturnToSlides}
            aria-label="Return to slides"
          >
            Return
          </button>
        </div>
      ) : null}
      {gameActive ? (
        <p className="app-loader__dog-score app-loader__dog-score--hud" aria-live="polite">
          <span className="app-loader__dog-score__segment">
            <span className="app-loader__dog-score__n">{score}</span> caught
          </span>
          <span
            className={`app-loader__dog-score__misses-pill${
              misses >= MAX_MISSES - 1 ? " app-loader__dog-score__misses-pill--warn" : ""
            }`}
          >
            <span className="app-loader__dog-score__misses-pill__label">Misses</span>
            <span className="app-loader__dog-score__misses-pill__n">
              {misses}
              <span className="app-loader__dog-score__misses-pill__sep">/</span>
              {MAX_MISSES}
            </span>
          </span>
          <span className="app-loader__dog-score__segment app-loader__dog-score__best">
            Best run <span className="app-loader__dog-score__n">{showBest}</span>
          </span>
        </p>
      ) : isGame && everStartedRun ? (
        <p className="app-loader__dog-score app-loader__dog-score--persistent" aria-live="polite">
          <span className="app-loader__dog-score__best">Best run</span>{" "}
          <span className="app-loader__dog-score__n">{showBest}</span>
        </p>
      ) : null}
    </div>
  );
}
